import { useEffect, useRef, useState } from 'react';
import { useDeployAccess, useDeployHistory, useSystemSummary } from '../../../api/queries';
import { useTriggerDeploy } from '../../../api/mutations';
import { useDeployStatus, type DeployStatusEvent } from '../../../api/socket';
import { DangerButton } from '../../../components/DangerButton';
import { Ic } from '../../../components/Ic';
import { dateTime, relTime } from '../../../lib/time';

const PHASE_PILL: Record<DeployStatusEvent['phase'], string> = {
  pulling: 'indigo',
  building: 'yellow',
  restarting: 'yellow',
  done: 'green',
  error: 'red',
};

export function DeployTab() {
  const { data: access } = useDeployAccess();
  const { data: history } = useDeployHistory();
  const { data: summary } = useSystemSummary();
  const deploy = useTriggerDeploy();

  const [lines, setLines] = useState<Array<{ phase: string; text: string }>>([]);
  const [phase, setPhase] = useState<DeployStatusEvent['phase'] | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useDeployStatus((event) => {
    setPhase(event.phase);
    if (event.log) {
      setLines((prev) => [...prev, { phase: event.phase, text: event.log! }].slice(-500));
    }
    if (event.summary) {
      setLines((prev) => [...prev, { phase: event.phase, text: event.summary! }].slice(-500));
    }
  });

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [lines.length]);

  const allowed = access?.allowed ?? false;
  const running = phase === 'pulling' || phase === 'building' || phase === 'restarting';

  return (
    <>
      <div className="pcard">
        <div className="head">
          <Ic.upload className="ic" />
          <h3>Deploy</h3>
          <span className="spacer" />
          {phase && <span className={`pill ${PHASE_PILL[phase]} dot`}>{phase}</span>}
        </div>
        <div className="body pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="muted" style={{ fontSize: 12.5 }}>
            Runs <code className="mono-sm">git pull</code> then{' '}
            <code className="mono-sm">docker compose up -d --build</code> on the host. The app
            restarts, so expect a short outage.
          </div>

          <div className="stat-row">
            <div>
              <div className="k muted" style={{ fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                Current build
              </div>
              <div className="mono" style={{ fontSize: 14 }}>
                {summary?.commit || '—'}
              </div>
            </div>
            <div>
              <div className="k muted" style={{ fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                Last deploy
              </div>
              <div className="mono" style={{ fontSize: 14 }}>
                {summary?.last_deploy ? relTime(summary.last_deploy.ts) : '—'}
              </div>
            </div>
          </div>

          {!allowed && (
            <div className="err">Deploying is restricted to the super admin.</div>
          )}

          <div>
            <DangerButton
              className="btn danger"
              disabled={!allowed || deploy.isPending || running}
              confirmLabel="Deploy now?"
              onConfirm={() => {
                setLines([]);
                setPhase(null);
                deploy.mutate();
              }}
            >
              <Ic.upload className="ico" /> Pull &amp; rebuild
            </DangerButton>
          </div>
        </div>
      </div>

      {lines.length > 0 && (
        <div className="pcard">
          <div className="head">
            <Ic.cmd className="ic" />
            <h3>Deploy log</h3>
          </div>
          <div className="logbox" ref={boxRef}>
            {lines.map((l, i) => (
              <div key={i} className={`logline ${l.phase === 'error' ? 'err' : ''}`}>
                <span className="lm">{l.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pcard">
        <div className="head">
          <Ic.history className="ic" />
          <h3>History</h3>
        </div>
        <div className="tbl-scroll">
          <table className="assign">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>By</th>
                <th>Phase</th>
              </tr>
            </thead>
            <tbody>
              {history?.length ? (
                history.map((h, i) => (
                  <tr key={`${h.ts}-${i}`}>
                    <td className="mono-sm">{dateTime(h.ts)}</td>
                    <td>{h.action}</td>
                    <td className="mono-sm">{h.requested_by}</td>
                    <td>
                      <span className={`pill ${h.phase === 'error' ? 'red' : h.phase === 'done' ? 'green' : ''}`}>
                        {h.phase}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="muted">
                    No deploys recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
